import React, { createContext, useContext, useState, useEffect } from 'react';

const CookieConsentContext = createContext();

export const useCookieConsent = () => {
    const context = useContext(CookieConsentContext);
    if (!context) {
        throw new Error('useCookieConsent must be used within CookieConsentProvider');
    }
    return context;
};

export const CookieConsentProvider = ({ children }) => {
    const [consent, setConsent] = useState({
        necessary: true, // Always true
        analytics: null, // null = not decided, true/false = user choice
        marketing: null
    });
    const [showBanner, setShowBanner] = useState(false);

    useEffect(() => {
        // Check if user has already made a choice
        const savedConsent = localStorage.getItem('cookieConsent');
        if (savedConsent) {
            const parsed = JSON.parse(savedConsent);
            setConsent(parsed);

            // Load scripts if analytics/marketing accepted
            if (parsed.analytics) {
                loadGoogleAnalytics();
            }
            if (parsed.marketing) {
                loadMarketingScripts();
            }
        } else {
            // Show banner after 15 seconds if no consent saved
            const timer = setTimeout(() => {
                setShowBanner(true);
            }, 15000);
            return () => clearTimeout(timer);
        }
    }, []);

    const acceptAll = () => {
        const newConsent = {
            necessary: true,
            analytics: true,
            marketing: true
        };
        setConsent(newConsent);
        localStorage.setItem('cookieConsent', JSON.stringify(newConsent));
        setShowBanner(false);

        // Load all tracking scripts
        loadGoogleAnalytics();
        loadMarketingScripts();
    };

    const rejectAll = () => {
        const newConsent = {
            necessary: true,
            analytics: false,
            marketing: false
        };
        setConsent(newConsent);
        localStorage.setItem('cookieConsent', JSON.stringify(newConsent));
        setShowBanner(false);
    };

    const savePreferences = (preferences) => {
        const newConsent = {
            necessary: true,
            analytics: preferences.analytics,
            marketing: preferences.marketing
        };
        setConsent(newConsent);
        localStorage.setItem('cookieConsent', JSON.stringify(newConsent));
        setShowBanner(false);

        // Load scripts based on preferences
        if (preferences.analytics) {
            loadGoogleAnalytics();
        }
        if (preferences.marketing) {
            loadMarketingScripts();
        }
    };

    const loadGoogleAnalytics = () => {
        // Google Analytics 4
        if (!window.gtag) {
            const script1 = document.createElement('script');
            script1.async = true;
            script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX'; // Replace with your GA4 ID
            document.head.appendChild(script1);

            const script2 = document.createElement('script');
            script2.innerHTML = `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', 'G-XXXXXXXXXX');
            `;
            document.head.appendChild(script2);
        }
    };

    const loadMarketingScripts = () => {
        // Meta Pixel
        if (!window.fbq) {
            const script = document.createElement('script');
            script.innerHTML = `
                !function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', 'YOUR_PIXEL_ID'); // Replace with your Meta Pixel ID
                fbq('track', 'PageView');
            `;
            document.head.appendChild(script);
        }
    };

    return (
        <CookieConsentContext.Provider value={{ consent, showBanner, setShowBanner, acceptAll, rejectAll, savePreferences }}>
            {children}
        </CookieConsentContext.Provider>
    );
};
